"use client";

import React, { useState } from "react";
import { Modal, Form, Input, Button, message } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { apiClient } from "@/lib/api/openings-client";

interface ContactFormData {
  name: string;
  email: string;
  message: string;
}

const CONTACT_HASH = "#contact";

const isContactHref = (href: string): boolean => {
  const normalized = href.trim().toLowerCase();
  // Only intercept hash-based contact triggers, not /contact page links
  return normalized.includes(CONTACT_HASH);
};

export default function ContactModalBridge({ salonId }: { salonId: string }) {
  const [open, setOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [form] = Form.useForm();
  const [msgApi, contextHolder] = message.useMessage();

  const consumeHash = React.useCallback(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash !== CONTACT_HASH) return;
    setOpen(true);
    setSuccess(false);
    const nextUrl = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState(null, "", nextUrl);
  }, []);

  const handleContactTrigger = React.useCallback((event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    const trigger = target?.closest?.(
      "[data-openings-contact-trigger]",
    ) as HTMLElement | null;
    const anchor = target?.closest?.("a") as HTMLAnchorElement | null;
    const href = anchor?.getAttribute("href") ?? "";
    const matchesContactHref = isContactHref(href);

    if (!trigger && !matchesContactHref) return;
    event.preventDefault();
    event.stopPropagation();
    setOpen(true);
    setSuccess(false);
    const nextUrl = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState(null, "", nextUrl);
  }, []);

  React.useEffect(() => {
    consumeHash();
    const onHashChange = () => consumeHash();
    window.addEventListener("hashchange", onHashChange);
    window.addEventListener("click", handleContactTrigger, true);
    return () => {
      window.removeEventListener("hashchange", onHashChange);
      window.removeEventListener("click", handleContactTrigger, true);
    };
  }, [consumeHash, handleContactTrigger]);

  const handleClose = React.useCallback(() => {
    setOpen(false);
    setSuccess(false);
    form.resetFields();
  }, [form]);

  const handleSubmit = React.useCallback(
    async (values: ContactFormData) => {
      setSubmitting(true);
      try {
        await apiClient.contact.send({
          salonId,
          subject: "general",
          name: values.name,
          email: values.email,
          phoneNumber: "",
          message: values.message,
        });

        setSuccess(true);
        window.setTimeout(() => {
          setOpen(false);
          setSuccess(false);
          form.resetFields();
        }, 3000);
      } catch (error: any) {
        msgApi.error(
          error?.message || "Failed to send message. Please try again.",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [form, msgApi, salonId],
  );

  return (
    <>
      {contextHolder}
      <Modal
        title={success ? "" : "Contact Us"}
        open={open}
        onCancel={handleClose}
        footer={null}
      >
        {success ? (
          <div style={{ textAlign: "center", padding: "40px 20px" }}>
            <CheckCircleOutlined
              style={{ fontSize: 48, color: "#52c41a", marginBottom: 16 }}
            />
            <h3 style={{ marginBottom: 8 }}>Message Sent Successfully</h3>
            <p style={{ opacity: 0.8 }}>
              Thank you for reaching out. We&apos;ll get back to you soon!
            </p>
            <Button
              onClick={handleClose}
              style={{ marginTop: 24 }}
              type="primary"
            >
              Close
            </Button>
          </div>
        ) : (
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: "Please enter your name" }]}
            >
              <Input placeholder="Your name" />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input type="email" placeholder="you@example.com" />
            </Form.Item>

            <Form.Item
              label="Message"
              name="message"
              rules={[{ required: true, message: "Please enter a message" }]}
            >
              <Input.TextArea rows={4} placeholder="How can we help you?" />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
              <div
                style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}
              >
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  disabled={submitting}
                >
                  Send
                </Button>
              </div>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </>
  );
}
